import dayjs from 'dayjs';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TopFilterComponent } from '../../../../shared/components/AppLayout/TopFilterComponent';
import { CustomTablePagination } from '../../../../shared/components/CustomTablePagination';
import errorHandler from '../../../../shared/utils/errorHandler';
import { IFilter } from './types';
import { topFilterUtil } from './utils';
import { IPageConfiguration } from '../../commonTypes';
import { getInvoices } from '../../../../shared/api/d2r';
import { D2RInvoiceSlider } from '../../../../shared/components/D2RInvoiceSlider';
import d2rInvoiceColumns from './columns';
import { companyType } from '../D2r';
import { apiBms } from '../../../../shared/api';
import { defaultPageConfig, EDOWNLOAD_ENTITY } from '../../../../constants';
import { defaultFilter } from './constants';
import { RequestEmailModal } from 'shared/components/EmailRequestModal';
import { envs } from 'shared/utils/env';
import { downloadAndMail, downloadCSV, downloadPaymentsCSV } from 'shared/api/orders';

export default function Invoices({
	company
}: {
	readonly company: Readonly<companyType>;
}) {
	const socketId = localStorage.getItem('socketId');
	const [data, setData] = useState<any[]>([]);
	const [currentCount, setCurrentCount] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [filter, setFilter] = useState<IFilter>(defaultFilter);
	const [pageConfig, setPageConfig] = useState<IPageConfiguration>(defaultPageConfig);
	const [showD2RInvoiceSlider, setShowD2RInvoiceSlider] = useState<string>('');
	const [defaultSalesmanId, setDefaultSalesmanId] = useState<string>('');
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [isVoucherXmlLoading, setIsVoucherXmlLoading] = useState(false);
	const [isVoucherExcelLoading, setIsVoucherExcelLoading] = useState(false);
	const [isEmailModalVisible, setIsEmailModalVisible] = useState<boolean>(false);
	const [isDownloadButtonLoading, setIsDownloadButtonLoading] = useState(false);
	const [isPaymentEmailModalVisible, setIsPaymentEmailModalVisible] = useState<boolean>(false);

	const columns = d2rInvoiceColumns(setShowD2RInvoiceSlider, setDefaultSalesmanId);
	const singleFieldMap = useMemo(
		() => ({
			order: defaultPageConfig.sortField,
			sortBy: defaultPageConfig.sortOrder,
			records: defaultPageConfig.pageSize,
			page: defaultPageConfig.current,
			company: company?.id ?? null
		}),
		[company]
	);

	const setPageConfigDefault = useCallback(() => {
		setPageConfig(defaultPageConfig);
	}, []);

	const onCSVDownload = useCallback(async () => {
		try {
			const csvFilter = { ...filter, ...singleFieldMap, appType: envs.appType };
			const shouldEmailOpen = await downloadCSV(EDOWNLOAD_ENTITY.INVOICES, csvFilter);
			setIsEmailModalVisible(shouldEmailOpen as boolean);
		} catch (err) {
			errorHandler(err);
		}
	}, [setIsEmailModalVisible, filter, singleFieldMap]);

	const onPaymentsDownload = () => {
		setIsPaymentEmailModalVisible(true);
	};

	const onInvoiceFormSubmit = useCallback(
		async (values: any) => {
			try {
				setIsDownloadButtonLoading(true);
				const csvFilter = {
					...filter,
					...singleFieldMap,
					appType: envs.appType
				};
				await downloadAndMail(EDOWNLOAD_ENTITY.INVOICES, csvFilter, values.email, socketId);
			} catch (e) {
			} finally {
				setIsDownloadButtonLoading(false);
				setIsEmailModalVisible(false);
			}
		},
		[filter, socketId, singleFieldMap]
	);

	const onPaymentFormSubmit = useCallback(
		async (values: any) => {
			try {
				setIsDownloadButtonLoading(true);
				const csvFilter = {
					start: filter.start,
					end: filter.end,
					sortBy: 'DESC',
					company: company?.id ?? null,
					appType: envs.appType
				};
				await downloadAndMail(EDOWNLOAD_ENTITY.PAYMENTS, csvFilter, values.email, socketId);
			} catch (e) {
			} finally {
				setIsDownloadButtonLoading(false);
				setIsPaymentEmailModalVisible(false);
			}
		},
		[filter, socketId]
	);

	const onDownloadOrdersVoucherXml = useCallback(async () => {
		try {
			if (selectedRowKeys.length === 0) {
				throw new Error('No order is selected.');
			}
			setIsVoucherXmlLoading(true);
			const { data } = await apiBms.post('/tally-voucher/download/:xml', {
				orderIds: selectedRowKeys
			});

			if (data.success) {
				const blob = new Blob([data.xml], { type: 'text/xml' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.download = `InvoiceVoucher${dayjs().format()}`;
				link.href = url;
				link.click();
				setIsVoucherXmlLoading(false);
			} else {
				throw new Error("Voucher for some orders can't be created. Download excel to know the reason.");
			}
		} catch (err) {
			setIsVoucherXmlLoading(false);
			errorHandler(err);
		}
	}, [selectedRowKeys]);

	const onDownloadOrdersVoucherExcel = useCallback(async () => {
		try {
			if (selectedRowKeys.length === 0) {
				throw new Error('No order is selected.');
			}

			setIsVoucherExcelLoading(true);
			const { data } = await apiBms.post('/tally-voucher/download/:excel', { orderIds: selectedRowKeys }, { responseType: 'blob' });

			const blob = new Blob([data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,'
			});
			let a = document.createElement('a');
			a.href = URL.createObjectURL(blob);
			a.download = `InvoiceVoucher${dayjs().format()}`;
			a.click();
			setIsVoucherExcelLoading(false);
		} catch (err) {
			setIsVoucherExcelLoading(false);
			errorHandler(err);
		}
	}, [selectedRowKeys]);

	const topFilterConfig = useMemo(
		() =>
			topFilterUtil({
				filter,
				setFilter,
				setPageConfigDefault,
				selectedRowKeys,
				onCSVDownload,
				onDownloadOrdersVoucherXml,
				onDownloadOrdersVoucherExcel,
				isVoucherXmlLoading,
				isVoucherExcelLoading,
				onPaymentsDownload
			}),
		[
			filter,
			setFilter,
			setPageConfigDefault,
			selectedRowKeys,
			isVoucherXmlLoading,
			isVoucherExcelLoading,
			onCSVDownload,
			onDownloadOrdersVoucherXml,
			onDownloadOrdersVoucherExcel
		]
	);

	const getAllInvoices = useCallback(
		async (pageConfiguration: any, filters: any) => {
			try {
				setLoading(true);

				const response = await getInvoices(pageConfiguration, filters);
				if (response?.data.status) {
					setData(response.data.invoices);
					setCurrentCount(response.data.currentCount);

					if (response.data.invoices.length === 1 && filter.searchValue !== '' && filter.searchOptionType === 'invoiceId') {
						setDefaultSalesmanId(response.data.invoices[0].d2rSalesmanId);
						setShowD2RInvoiceSlider(response.data.invoices.length ? response.data.invoices[0].id : '');
					}
				} else {
					throw new Error(response?.data.message ?? 'Could not get payments');
				}
			} catch (error: unknown) {
				errorHandler(error);
			} finally {
				setLoading(false);
			}
		},
		[filter.searchOptionType, filter.searchValue]
	);

	useEffect(() => {
		setFilter((prevFilter: IFilter) => ({ ...prevFilter, company }));
	}, [company]);

	useEffect(() => {
		getAllInvoices(pageConfig, filter);
	}, [filter, pageConfig, getAllInvoices]);

	return (
		<Row className="h-100 p-0">
			<RequestEmailModal
				title="Download Payments"
				visible={isPaymentEmailModalVisible}
				onCancel={() => {
					setIsPaymentEmailModalVisible(false);
				}}
				footer={[]}
				width={500}
				onFinish={onPaymentFormSubmit}
				instructionMessage="Requested data contains too many rows, please enter your email address to receive download url."
				loading={isDownloadButtonLoading}
			/>
			<RequestEmailModal
				title="Download Invoices"
				visible={isEmailModalVisible}
				onCancel={() => {
					setIsEmailModalVisible(false);
				}}
				footer={[]}
				width={500}
				onFinish={onInvoiceFormSubmit}
				instructionMessage="Requested data contains too many rows, please enter your email address to receive download url."
				loading={isDownloadButtonLoading}
			/>
			<Col span={24} className="main">
				<TopFilterComponent {...topFilterConfig} />
				<div className="innerDiv">
					<div className="div w-100 h-100">
						<CustomTablePagination
							columns={columns}
							data={data}
							onChangePage={(current: any, pageSize: any) => setPageConfig((v) => ({ ...v, current, pageSize }))}
							current={Number(pageConfig.current)}
							total={currentCount}
							loading={loading}
							shouldRowSelection={true}
							selectedItem={undefined}
							catalog={undefined}
							selectedRowKeys={undefined}
							setSelectedRowKeys={setSelectedRowKeys}
							setSelectedItem={undefined}
							pageSize={Number(pageConfig.pageSize)}
							sortField={pageConfig.sortField}
							sortOrder={pageConfig.sortField}
						/>
					</div>
				</div>
			</Col>
			{showD2RInvoiceSlider && (
				<D2RInvoiceSlider
					orderId={showD2RInvoiceSlider}
					salesmanId={defaultSalesmanId}
					onClose={() => {
						setShowD2RInvoiceSlider('');
					}}
					onPaymentCaptured={() => {
						getAllInvoices(pageConfig, filter);
					}}
					key={'#0'}
				/>
			)}
		</Row>
	);
}
